import type { CalendarGrid } from '@peach/types/schedule/types.js';
import dayjs, { type Dayjs } from 'dayjs';
import { guessTimezone } from './date';

type DayjsTimeSlots = Record<string, Record<string, { start: Dayjs; end: Dayjs }[]>>;

export function getTzSchedule(
  available: CalendarGrid['available'],
  limit?: Dayjs,
  timezone?: string
) {
  const viewerTimezone = timezone ?? guessTimezone();
  const tzSchedule: DayjsTimeSlots = {};

  for (const slots of Object.values(available)) {
    for (const slot of slots) {
      const localStart = dayjs.utc(slot.start).tz(viewerTimezone);
      const localEnd = dayjs.utc(slot.end).tz(viewerTimezone);

      if (limit && localStart.isAfter(limit)) continue;

      const tzMonth = localStart.month().toString();
      const tzDay = localStart.date().toString();

      if (!tzSchedule[tzMonth]) tzSchedule[tzMonth] = {};
      if (!tzSchedule[tzMonth][tzDay]) tzSchedule[tzMonth][tzDay] = [];
      tzSchedule[tzMonth][tzDay].push({ start: localStart, end: localEnd });
    }
  }

  for (const month of Object.keys(tzSchedule)) {
    for (const day of Object.keys(tzSchedule[month])) {
      tzSchedule[month][day].sort((a, b) => a.start.valueOf() - b.start.valueOf());
    }
  }

  // Sort months by their earliest slot date to handle year boundaries correctly
  const months = Object.keys(tzSchedule).sort((a, b) => {
    const earliestA = getEarliestSlot(tzSchedule[a]);
    const earliestB = getEarliestSlot(tzSchedule[b]);
    return earliestA.valueOf() - earliestB.valueOf();
  });

  const days = Object.fromEntries(
    Object.entries(tzSchedule).map(([month, monthDays]) => [
      month,
      Object.keys(monthDays).sort((a, b) => Number(a) - Number(b)),
    ])
  );

  return {
    tzSchedule,
    combinedSlots: combineContinuousSlots(tzSchedule),
    months,
    days,
  };
}

function getEarliestSlot(monthDays: Record<string, { start: Dayjs; end: Dayjs }[]>): Dayjs {
  let earliest: Dayjs | null = null;
  for (const slots of Object.values(monthDays)) {
    for (const slot of slots) {
      if (!earliest || slot.start.isBefore(earliest)) {
        earliest = slot.start;
      }
    }
  }
  return earliest!;
}

function combineContinuousSlots(tzSchedule: DayjsTimeSlots): DayjsTimeSlots {
  const combined: DayjsTimeSlots = {};

  for (const [month, monthDays] of Object.entries(tzSchedule)) {
    combined[month] = {};

    for (const [day, slots] of Object.entries(monthDays)) {
      if (slots.length === 0) continue;

      const merged: { start: Dayjs; end: Dayjs }[] = [];
      let current = { ...slots[0] };

      for (let i = 1; i < slots.length; i++) {
        const next = slots[i];
        if (current.end.isSame(next.start) || current.end.isAfter(next.start)) {
          if (next.end.isAfter(current.end)) current.end = next.end;
        } else {
          merged.push(current);
          current = { ...next };
        }
      }
      merged.push(current);

      combined[month][day] = merged;
    }
  }

  return combined;
}
